import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, isDRXError } from '@/lib/drx-error';
import type {
  IJobRepository,
  CreateJobData,
  GetJobData,
  UpdateJobData,
  JobSearchFilters,
  CreateJobApplicationData,
  GetJobApplicationData,
  JobApplicationStatus,
  JobStatistics,
} from './interfaces/job.repository.interface';

export class JobRepository implements IJobRepository {
  private jobsCollection = collection(db, 'jobs');
  private applicationsCollection = collection(db, 'jobApplications');

  async createJob(jobData: CreateJobData): Promise<GetJobData> {
    try {
      const docRef = await addDoc(this.jobsCollection, {
        ...jobData,
        applicantCount: 0,
        viewCount: 0,
        shortlistedApplicants: [],
        featured: false,
        boostLevel: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const created = await this.getJob(docRef.id);
      if (!created) {
        throw new DRXError({
          code: 'JOB_CREATE_FAILED',
          devMessage: `Job created with id ${docRef.id} but could not be read back`,
          userMessage: 'Could not create your job posting. Please try again.',
          level: 'error',
        });
      }
      return created;
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_CREATE_FAILED',
        devMessage: 'Failed to create job posting in Firestore',
        userMessage: 'Could not create your job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJob(jobId: string): Promise<GetJobData | null> {
    try {
      const jobDoc = await getDoc(doc(this.jobsCollection, jobId));
      if (!jobDoc.exists()) return null;
      return { id: jobDoc.id, ...jobDoc.data() } as GetJobData;
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_FETCH_FAILED',
        devMessage: `Failed to fetch job ${jobId}`,
        userMessage: 'Could not load the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobsByCreator(creatorId: string, status?: string): Promise<GetJobData[]> {
    try {
      const constraints: any[] = [where('creatorId', '==', creatorId)];
      if (status) {
        constraints.push(where('status', '==', status));
      }
      constraints.push(orderBy('createdAt', 'desc'));

      const jobQuery = query(this.jobsCollection, ...constraints);
      const snapshot = await getDocs(jobQuery);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as GetJobData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_FETCH_FAILED',
        devMessage: `Failed to fetch jobs for creator ${creatorId}`,
        userMessage: 'Could not load your job postings. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async searchJobs(filters: JobSearchFilters, limitCount = 20): Promise<GetJobData[]> {
    try {
      // The app writes status='open' on creation. Accept both legacy ('active') and current ('open').
      // visibility filter happens client-side below — Firestore can't combine `in` with `!=` cleanly,
      // and many existing docs lack the field entirely.
      let jobQuery = query(
        this.jobsCollection,
        where('status', 'in', ['open', 'active']),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      if (filters.category && filters.category.length > 0) {
        jobQuery = query(jobQuery, where('category', 'in', filters.category));
      }

      if (filters.locationType && filters.locationType.length > 0) {
        jobQuery = query(jobQuery, where('locationType', 'in', filters.locationType));
      }

      const snapshot = await getDocs(jobQuery);
      let jobs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as GetJobData);

      // Client-side visibility filter: missing field treated as public for legacy docs.
      jobs = jobs.filter((job) => {
        const v = (job as unknown as { visibility?: string }).visibility;
        return v === undefined || v === 'public' || v === 'members-only';
      });

      // Client-side filtering for fields that cannot be combined in a single Firestore query
      if (filters.danceStyles && filters.danceStyles.length > 0) {
        jobs = jobs.filter((job) =>
          job.danceStyles.some((style) => filters.danceStyles!.includes(style))
        );
      }

      if (filters.minRate || filters.maxRate) {
        jobs = jobs.filter((job) => {
          if (!job.rate) return false;
          if (filters.minRate && job.rate < filters.minRate) return false;
          if (filters.maxRate && job.rate > filters.maxRate) return false;
          return true;
        });
      }

      return jobs;
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SEARCH_FAILED',
        devMessage: 'Failed to search jobs',
        userMessage: 'Could not search job postings. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateJob(jobId: string, updateData: UpdateJobData): Promise<void> {
    try {
      const jobRef = doc(this.jobsCollection, jobId);
      await updateDoc(jobRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_UPDATE_FAILED',
        devMessage: `Failed to update job ${jobId}`,
        userMessage: 'Could not update the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteJob(jobId: string): Promise<void> {
    try {
      await deleteDoc(doc(this.jobsCollection, jobId));
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_DELETE_FAILED',
        devMessage: `Failed to delete job ${jobId}`,
        userMessage: 'Could not delete the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async applyToJob(applicationData: CreateJobApplicationData): Promise<string> {
    try {
      const docRef = await addDoc(this.applicationsCollection, {
        ...applicationData,
        messages: [],
        appliedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Increment the job's applicant count
      const jobRef = doc(this.jobsCollection, applicationData.jobId);
      await updateDoc(jobRef, {
        applicantCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      return docRef.id;
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_FAILED',
        devMessage: `Failed to apply to job ${applicationData.jobId}`,
        userMessage: 'Could not submit your application. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobApplications(jobId: string): Promise<GetJobApplicationData[]> {
    try {
      const q = query(
        this.applicationsCollection,
        where('jobId', '==', jobId),
        orderBy('appliedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as GetJobApplicationData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_FETCH_FAILED',
        devMessage: `Failed to fetch applications for job ${jobId}`,
        userMessage: 'Could not load job applications. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateApplicationStatus(
    applicationId: string,
    status: JobApplicationStatus,
    employerNotes?: string
  ): Promise<void> {
    try {
      const applicationRef = doc(this.applicationsCollection, applicationId);
      await updateDoc(applicationRef, {
        status,
        ...(employerNotes !== undefined && { employerNotes }),
        updatedAt: serverTimestamp(),
        respondedAt: serverTimestamp(),
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_UPDATE_FAILED',
        devMessage: `Failed to update application status for ${applicationId}`,
        userMessage: 'Could not update the application status. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getUserApplications(userId: string): Promise<GetJobApplicationData[]> {
    try {
      const q = query(
        this.applicationsCollection,
        where('applicantId', '==', userId),
        orderBy('appliedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as GetJobApplicationData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_FETCH_FAILED',
        devMessage: `Failed to fetch applications for user ${userId}`,
        userMessage: 'Could not load your applications. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobStatistics(creatorId: string): Promise<JobStatistics> {
    try {
      const jobs = await this.getJobsByCreator(creatorId);
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      return {
        totalJobs: jobs.length,
        // Jobs are created with status='open'; 'active' is a legacy value from
        // before that rename. Accept both, matching searchJobs().
        activeJobs: jobs.filter((job) => job.status === 'open' || job.status === 'active').length,
        filledJobs: jobs.filter((job) => job.status === 'filled').length,
        totalApplications: jobs.reduce((sum, job) => sum + (job.applicantCount || 0), 0),
        thisMonthJobs: jobs.filter((job) => {
          const createdAt = job.createdAt?.toDate?.();
          return createdAt && createdAt >= thisMonth;
        }).length,
      };
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_STATS_FAILED',
        devMessage: `Failed to compute job statistics for creator ${creatorId}`,
        userMessage: 'Could not load job statistics. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async incrementViewCount(jobId: string): Promise<void> {
    try {
      const jobRef = doc(this.jobsCollection, jobId);
      await updateDoc(jobRef, {
        viewCount: increment(1),
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_UPDATE_FAILED',
        devMessage: `Failed to increment view count for job ${jobId}`,
        userMessage: 'Could not update view count.',
        cause: error,
        level: 'warning',
      });
    }
  }

  async incrementApplicants(jobId: string): Promise<void> {
    try {
      const jobRef = doc(this.jobsCollection, jobId);
      await updateDoc(jobRef, {
        applicantCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_UPDATE_FAILED',
        devMessage: `Failed to increment applicant count for job ${jobId}`,
        userMessage: 'Could not update applicant count.',
        cause: error,
        level: 'warning',
      });
    }
  }

  async createJobBoardDocument(payload: Record<string, unknown>): Promise<string> {
    try {
      const docRef = await addDoc(this.jobsCollection, {
        ...payload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_CREATE_FAILED',
        devMessage: 'Failed to create job board document',
        userMessage: 'Could not create the job posting.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobBoardDocument(jobId: string): Promise<(Record<string, unknown> & { id: string }) | null> {
    try {
      const jobDoc = await getDoc(doc(this.jobsCollection, jobId));
      if (!jobDoc.exists()) return null;
      return { id: jobDoc.id, ...jobDoc.data() } as Record<string, unknown> & { id: string };
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_FETCH_FAILED',
        devMessage: `Failed to fetch job board document ${jobId}`,
        userMessage: 'Could not load the job posting.',
        cause: error,
        level: 'error',
      });
    }
  }

  async listJobBoardDocumentsByCreator(
    creatorId: string,
  ): Promise<Array<Record<string, unknown> & { id: string }>> {
    try {
      const jobQuery = query(this.jobsCollection, where('creatorId', '==', creatorId));
      const querySnapshot = await getDocs(jobQuery);
      const jobs = querySnapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Record<string, unknown> & { id: string },
      );
      jobs.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt as string).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt as string).getTime() : 0;
        return bTime - aTime;
      });
      return jobs;
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_LIST_CREATOR_FAILED',
        devMessage: `Failed to list job board documents for creator ${creatorId}`,
        userMessage: 'Could not load job postings.',
        cause: error,
        level: 'error',
      });
    }
  }

  async listJobBoardDocumentsExcludingStatus(
    excludedStatus: string,
  ): Promise<Array<Record<string, unknown> & { id: string }>> {
    try {
      const jobQuery = query(this.jobsCollection, where('status', '!=', excludedStatus));
      const querySnapshot = await getDocs(jobQuery);
      const jobs = querySnapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Record<string, unknown> & { id: string },
      );
      jobs.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt as string).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt as string).getTime() : 0;
        return bTime - aTime;
      });
      return jobs;
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_LIST_OPEN_FAILED',
        devMessage: 'Failed to list open job board documents',
        userMessage: 'Could not load job postings.',
        cause: error,
        level: 'error',
      });
    }
  }

  async patchJobBoardDocument(jobId: string, patch: Record<string, unknown>): Promise<void> {
    try {
      await updateDoc(doc(this.jobsCollection, jobId), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_PATCH_FAILED',
        devMessage: `Failed to update job board document ${jobId}`,
        userMessage: 'Could not update the job posting.',
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteJobBoardDocument(jobId: string): Promise<void> {
    try {
      await deleteDoc(doc(this.jobsCollection, jobId));
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_DELETE_FAILED',
        devMessage: `Failed to delete job board document ${jobId}`,
        userMessage: 'Could not delete the job posting.',
        cause: error,
        level: 'error',
      });
    }
  }

  async incrementJobBoardApplicants(jobId: string, amount: number = 1): Promise<void> {
    try {
      await updateDoc(doc(this.jobsCollection, jobId), {
        applicants: increment(amount),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_APPLICANTS_FAILED',
        devMessage: `Failed to increment applicants for job ${jobId}`,
        cause: error,
        level: 'warning',
      });
    }
  }

  async incrementJobBoardViews(jobId: string, amount: number = 1): Promise<void> {
    try {
      await updateDoc(doc(this.jobsCollection, jobId), {
        views: increment(amount),
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_BOARD_VIEWS_FAILED',
        devMessage: `Failed to increment views for job ${jobId}`,
        cause: error,
        level: 'warning',
      });
    }
  }
}
